import { Editor, Element, Transforms, Range, Path } from 'slate';
import AhaEditor from '../../AhaEditor';
import TableUtils from '../TablePlugin/TableUtils';

export const getInlineElement = (editor: Editor) => {
  if (!editor.selection) {
    return;
  }

  return Editor.above(editor, {
    at: editor.selection.focus,
    mode: 'lowest',
    match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
  });
};

export const isWithinInline = (editor: Editor) => !!getInlineElement(editor);

export const isInlineActive = (editor: Editor, type: string) => {
  const ahaEditor = AhaEditor.getForEditor(editor);
  const tableSelection =
    ahaEditor?.controller?.state.pluginState.tableSelection;
  const tableWithinSelection = TableUtils.isTableWithinSelection(editor);

  if (tableWithinSelection && tableSelection) {
    return TableUtils.tableSelectionContainsInline(
      editor,
      tableSelection,
      type
    );
  }

  const [entry] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && Element.isElement(n) && n.type === type,
  });
  return !!entry;
};

export const unwrapInline = (editor: Editor, type: string) => {
  const ahaEditor = AhaEditor.getForEditor(editor);
  const tableSelection =
    ahaEditor?.controller?.state.pluginState.tableSelection;
  const tableWithinSelection = TableUtils.isTableWithinSelection(editor);

  const unwrapNodes = () =>
    Transforms.unwrapNodes(editor, {
      match: (n) => Element.isElement(n) && n.type === type,
    });

  if (tableWithinSelection && tableSelection) {
    // Apply the inline to the selected cells
    TableUtils.applyToTable(editor, tableSelection, unwrapNodes);
  } else {
    unwrapNodes();
  }
};

export const wrapInline = (
  editor: Editor,
  buildInline: () => Element,
  collapse = true
) => {
  if (!editor.selection) return;

  if (Range.isCollapsed(editor.selection)) {
    Editor.withoutNormalizing(editor, () => {
      if (!editor.selection) return;

      Transforms.splitNodes(editor, {
        match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
        at: editor.selection.focus,
      });

      const inlineAbove = Editor.above(editor, {
        match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
        at: editor.selection.focus,
      });

      if (inlineAbove) {
        const atStartOfInline = Editor.isStart(
          editor,
          editor.selection.focus,
          inlineAbove[1]
        );
        const textLocation = atStartOfInline
          ? inlineAbove[1]
          : Path.next(inlineAbove[1]);

        Transforms.insertNodes(editor, { text: '' }, { at: textLocation });

        const newEndPoint = Editor.end(editor, textLocation);

        Transforms.setSelection(editor, {
          anchor: newEndPoint,
          focus: newEndPoint,
        });
      }

      Transforms.insertNodes(editor, buildInline());
    });
  } else {
    const ahaEditor = AhaEditor.getForEditor(editor);
    const tableSelection =
      ahaEditor?.controller?.state.pluginState.tableSelection;
    const tableWithinSelection = TableUtils.isTableWithinSelection(editor);

    const wrapNodes = () => {
      Editor.withoutNormalizing(editor, () => {
        if (!editor.selection) return;

        const endPoint = Range.end(editor.selection);
        const inlineAboveEnd = Editor.above(editor, {
          match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
          at: endPoint,
        });
        if (inlineAboveEnd) {
          Transforms.splitNodes(editor, {
            match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
            at: endPoint,
          });

          const previous = Editor.previous(editor, {
            at: endPoint,
            match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
          });
          if (!previous) return;
          const newEndPoint = Editor.end(editor, previous[1]);

          const prop = Range.isBackward(editor.selection) ? 'anchor' : 'focus';
          Transforms.setSelection(editor, {
            [prop]: newEndPoint,
          });
        }

        const startPoint = Range.start(editor.selection);
        const inlineAboveStart = Editor.above(editor, {
          match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
          at: startPoint,
        });

        if (inlineAboveStart) {
          Transforms.splitNodes(editor, {
            match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
            at: startPoint,
          });
        }

        Transforms.unwrapNodes(editor, {
          match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
          at: editor.selection,
        });

        Transforms.wrapNodes(editor, buildInline(), {
          split: true,
        });
      });
    };

    if (tableWithinSelection && tableSelection) {
      // Apply the inline to the selected cells
      TableUtils.applyToTable(editor, tableSelection, wrapNodes);
      collapse = false;
    } else {
      wrapNodes();
    }

    if (collapse) {
      Transforms.collapse(editor, { edge: 'end' });
    }
  }
};
